/**
 * List of unsupported country codes (2-letter ISO codes)
 * Routes to/from these countries should show as unavailable
 */
export const UNSUPPORTED_COUNTRIES = ['RU', 'CN']

/**
 * Checks if a route includes an unsupported country (origin or destination)
 * @param addresses - Array of address data from booking widget context
 * @returns true if origin or destination is in an unsupported country
 */
export function hasUnsupportedCountry(addresses: any[] | undefined): boolean {
  if (!addresses?.[0] || addresses[0].length < 2) {
    return false
  }

  const originCountryCode = addresses[0][0]?.country_code?.toUpperCase()
  const destinationCountryCode = addresses[0][1]?.country_code?.toUpperCase()

  return (
    (originCountryCode && UNSUPPORTED_COUNTRIES.includes(originCountryCode)) ||
    (destinationCountryCode &&
      UNSUPPORTED_COUNTRIES.includes(destinationCountryCode))
  )
}

/**
 * Checks if ship route segments include an unsupported country (origin or destination)
 * @param segments - Array of address segments (origin at index 0, destination at index 1)
 * @returns true if origin or destination is in an unsupported country
 */
export function hasUnsupportedCountryInSegments(
  segments: Array<{ country_code?: string }> | undefined,
): boolean {
  if (!segments || segments.length < 2) {
    return false
  }

  const originCountryCode = segments[0]?.country_code?.toUpperCase()
  const destinationCountryCode = segments[1]?.country_code?.toUpperCase()

  return (
    Boolean(
      originCountryCode && UNSUPPORTED_COUNTRIES.includes(originCountryCode),
    ) ||
    Boolean(
      destinationCountryCode &&
        UNSUPPORTED_COUNTRIES.includes(destinationCountryCode),
    )
  )
}

/**
 * Checks if a route is unavailable based on addresses, origin/destination selection, and available service options
 * @param addresses - Array of address data from booking widget context
 * @param origin - Selected origin location
 * @param destination - Selected destination location
 * @param serviceOptions - Available service options for the route
 * @param isLoading - Whether service options are currently loading
 * @param hasAttemptedFetch - Whether we've actually attempted to fetch service options (e.g., products are selected)
 * @returns true if route is unavailable (valid addresses selected but no service options available after loading)
 */
export function isRouteUnavailable(
  addresses: any[] | undefined,
  origin: any,
  destination: any,
  serviceOptions: any[] | undefined,
  isLoading?: boolean,
  hasAttemptedFetch?: boolean,
): boolean {
  // Don't show unavailable message while loading
  if (isLoading) {
    return false
  }

  // Don't show unavailable message if we haven't attempted to fetch yet
  // (e.g., on homepage booking widget where products haven't been selected)
  if (hasAttemptedFetch === false) {
    return false
  }

  // Check if addresses are valid (have required fields including country code)
  const hasValidAddresses = addresses?.[0]?.every(
    (address: any) =>
      !!address?.city && !!address?.country_code && !!address?.state,
  )

  // Ensure both origin and destination are actually selected (not just typed)
  const hasSelectedLocations = !!origin && !!destination

  // Route is unavailable if:
  // 1. We have valid addresses with all required fields
  // 2. Both origin and destination are selected
  // 3. serviceOptions is explicitly an empty array (meaning it was fetched but returned no results)
  // 4. We're not currently loading
  // 5. We've actually attempted to fetch (products are selected)
  return (
    hasValidAddresses &&
    hasSelectedLocations &&
    Array.isArray(serviceOptions) &&
    serviceOptions.length === 0
  )
}

/**
 * Checks if a ship route is unavailable based on route validity and available products
 * @param isValidRoute - Whether the route is valid (from isValidShipRoute function)
 * @param products - Available products for the route
 * @returns true if route is unavailable (valid route but no products available)
 */
export function isShipRouteUnavailable(
  isValidRoute: boolean,
  products: any[] | undefined,
): boolean {
  return isValidRoute && !!products && products.length === 0
}
